import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDispatches, openDispatchPdf, openPalletLabels, openCoaPdf, mintDownloadToken } from '../lib/api';
import { openPrintWindow } from '../lib/printWindow';
import AldiBranchDialog from '../components/AldiBranchDialog';
import AccountantViewDialog from '../components/AccountantViewDialog';
import { InvoicePreviewDialog } from '../components/accountsiq/InvoicePreviewDialog';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Loader2, 
  Truck, 
  Calendar, 
  Download,
  Printer,
  Eye,
  FileText,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Tags,
  Package,
  ScrollText,
  FileSpreadsheet,
  Send,
  CircleCheck
} from 'lucide-react';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth,
  endOfMonth,
  addWeeks, 
  subWeeks,
  addMonths,
  subMonths, 
  getWeek, 
  getYear, 
  parse,
  isWithinInterval
} from 'date-fns';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function DispatchView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aldiBranchOpen, setAldiBranchOpen] = useState(false);
  const [aldiBranchDispatchId, setAldiBranchDispatchId] = useState(null);
  // AccountsIQ invoice helper — opened from the row 3-dot menu / detail
  // modal. Stashes the whole dispatch object so the dialog can do its
  // own aggregation without re-fetching.
  const [accountantDispatch, setAccountantDispatch] = useState(null);
  const [accountantOpen, setAccountantOpen] = useState(false);
  const [aiqPreviewDispatch, setAiqPreviewDispatch] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail modal
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'

  // Calculate date range based on viewMode
  const rangeStart = viewMode === 'week'
    ? startOfWeek(currentDate, { weekStartsOn: 1 })
    : startOfMonth(currentDate);
  const rangeEnd = viewMode === 'week'
    ? endOfWeek(currentDate, { weekStartsOn: 1 })
    : endOfMonth(currentDate);
  const weekNumber = getWeek(currentDate, { weekStartsOn: 1 });
  const year = getYear(currentDate);

  useEffect(() => {
    loadDispatches();
  }, []);

  const loadDispatches = async () => {
    try {
      setLoading(true);
      const response = await getDispatches({ status: 'dispatched,locked' });
      setDispatches(response.data || []);
    } catch (error) {
      toast.error('Failed to load dispatches');
    } finally {
      setLoading(false);
    }
  };

  // Filter and group dispatches by week then by date
  const groupedDispatches = useMemo(() => {
    // Filter dispatches within current week
    const filtered = dispatches.filter(d => {
      if (!d.dispatch_date) return false;
      try {
        const dispatchDate = parse(d.dispatch_date, 'dd/MM/yyyy', new Date());
        const inWeek = isWithinInterval(dispatchDate, { start: rangeStart, end: rangeEnd });
        
        // Also apply search filter
        if (searchTerm && inWeek) {
          const term = searchTerm.toLowerCase();
          return (
            d.dispatch_number?.toLowerCase().includes(term) ||
            d.customer_name?.toLowerCase().includes(term) ||
            d.po_number?.toLowerCase().includes(term)
          );
        }
        return inWeek;
      } catch {
        return false;
      }
    });

    // Group by date
    const byDate = {};
    filtered.forEach(d => {
      const date = d.dispatch_date;
      if (!byDate[date]) {
        byDate[date] = [];
      }
      byDate[date].push(d);
    });

    // Sort dates and dispatches within each date
    const sortedDates = Object.keys(byDate).sort((a, b) => {
      const dateA = parse(a, 'dd/MM/yyyy', new Date());
      const dateB = parse(b, 'dd/MM/yyyy', new Date());
      return dateA - dateB;
    });

    return sortedDates.map(date => ({
      date,
      dispatches: byDate[date].sort((a, b) => 
        (a.dispatch_number || '').localeCompare(b.dispatch_number || '')
      )
    }));
  }, [dispatches, rangeStart, rangeEnd, searchTerm]);

  // Calculate totals — Ordered vs Production (Dispatched)
  const periodTotals = useMemo(() => {
    const allDispatches = groupedDispatches.flatMap(g => g.dispatches);
    return {
      count: allDispatches.length,
      // Ordered metrics (from PO / Sales Order Entry)
      orderedQty: allDispatches.reduce((sum, d) => sum + (d.total_ordered_qty || 0), 0),
      orderedCases: allDispatches.reduce((sum, d) => sum + (d.total_ordered_cases || 0), 0),
      orderedWeight: allDispatches.reduce((sum, d) => sum + (d.total_ordered_weight || 0), 0),
      // Production metrics (from Pipeline > Ready > Batch Details)
      totalCases: allDispatches.reduce((sum, d) => sum + (d.total_cases || 0), 0),
      totalWeight: allDispatches.reduce((sum, d) => sum + (d.total_net_weight || 0), 0),
      totalGrossWeight: allDispatches.reduce((sum, d) => sum + (d.total_gross_weight || 0), 0),
    };
  }, [groupedDispatches]);

  const goPrev = () => setCurrentDate(viewMode === 'week' ? subWeeks(currentDate, 1) : subMonths(currentDate, 1));
  const goNext = () => setCurrentDate(viewMode === 'week' ? addWeeks(currentDate, 1) : addMonths(currentDate, 1));
  const goToCurrent = () => setCurrentDate(new Date());

  const openDetailModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    setDetailModalOpen(true);
  };

  // Compute a line-level total for a given field (fallback for missing dispatch-level totals)
  const computeTotal = (dispatch, field) => {
    if (!dispatch?.lines) return 0;
    return dispatch.lines.reduce((sum, l) => sum + (parseFloat(l[field]) || 0), 0);
  };

  const getProductSummary = (dispatch) => {
    const products = dispatch.lines?.map(l => l.product_name).filter(Boolean) || [];
    if (products.length === 0) return '-';
    if (products.length <= 2) return products.join(', ');
    return `${products.slice(0, 2).join(', ')} +${products.length - 2}`;
  };

  const viewDispatchPDF = async (dispatchId) => {
    await openDispatchPdf(dispatchId);
  };

  const printDispatchPDF = async (dispatchId) => {
    // Mint a single-use DLT then load into an offscreen iframe to trigger print.
    try {
      const dlt = await mintDownloadToken('dispatch_pdf_print', dispatchId);
      const url = `${API_BASE}/api/dispatches/${dispatchId}/download-pdf?token=${encodeURIComponent(dlt)}`;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 500);
      };
    } catch {
      toast.error('Failed to prepare printable PDF');
    }
  };

  const viewPalletLabels = async (dispatchId) => {
    await openPalletLabels(dispatchId, 2);
  };

  const printAllDocuments = async (dispatchId) => {
    await openDispatchPdf(dispatchId);
    setTimeout(() => {
      navigate(`/pallet-labels/${dispatchId}`);
    }, 500);
  };

  // Export to CSV
  const exportToCSV = () => {
    const allDispatches = groupedDispatches.flatMap(g => g.dispatches);
    if (allDispatches.length === 0) {
      toast.error('No dispatches to export');
      return;
    }

    let csvContent = 'Date,Dispatch Number,Customer,PO Number,Cases,Net Weight (kg),Products\n';
    
    allDispatches.forEach(d => {
      const products = d.lines?.map(l => l.product_name).filter(Boolean).join('; ') || '';
      csvContent += `"${d.dispatch_date}","${d.dispatch_number}","${d.customer_name}","${d.po_number || ''}",${(d.total_cases || 0).toFixed(2)},${(d.total_net_weight || 0).toFixed(2)},"${products}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatches_${viewMode === 'week' ? `week${weekNumber}` : format(currentDate, 'MMM_yyyy')}_${year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Export to PDF with proper title and page numbering
  const exportToPDF = () => {
    const allDispatches = groupedDispatches.flatMap(g => g.dispatches);
    if (allDispatches.length === 0) {
      toast.error('No dispatches to export');
      return;
    }

    const reportTitle = viewMode === 'week'
      ? `Weekly Dispatch Report - Week ${weekNumber}, ${year}`
      : `Monthly Dispatch Report - ${format(currentDate, 'MMMM yyyy')}`;
    const dateRange = `${format(rangeStart, 'dd MMM yyyy')} - ${format(rangeEnd, 'dd MMM yyyy')}`;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 15mm 10mm 20mm 10mm;
            }
            
            @media print {
              html, body {
                height: 100%;
                margin: 0;
                padding: 0;
              }
              .page-break {
                page-break-before: always;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              color: #333;
              line-height: 1.4;
            }
            
            .header {
              position: running(header);
              border-bottom: 2px solid #2563eb;
              padding-bottom: 8px;
              margin-bottom: 15px;
            }
            
            .header-title {
              font-size: 18px;
              font-weight: bold;
              color: #1e40af;
              margin: 0;
            }
            
            .header-subtitle {
              font-size: 12px;
              color: #64748b;
              margin: 4px 0 0 0;
            }
            
            .summary-box {
              display: flex;
              gap: 30px;
              margin: 15px 0;
              padding: 12px 20px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
            }
            
            .summary-item {
              text-align: center;
            }
            
            .summary-value {
              font-size: 22px;
              font-weight: bold;
              color: #1e293b;
            }
            
            .summary-value.highlight {
              color: #2563eb;
            }
            
            .summary-label {
              font-size: 10px;
              color: #64748b;
              text-transform: uppercase;
            }
            
            .date-section {
              margin: 20px 0 10px 0;
              padding: 8px 12px;
              background: #f1f5f9;
              border-left: 4px solid #2563eb;
              font-weight: bold;
              font-size: 12px;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
              font-size: 10px;
            }
            
            th {
              background: #1e40af;
              color: white;
              padding: 8px 6px;
              text-align: left;
              font-weight: 600;
              text-transform: uppercase;
              font-size: 9px;
            }
            
            th.right, td.right {
              text-align: right;
            }
            
            td {
              padding: 7px 6px;
              border-bottom: 1px solid #e2e8f0;
            }
            
            tr:nth-child(even) {
              background: #f8fafc;
            }
            
            .dispatch-num {
              font-family: monospace;
              font-weight: 600;
              color: #2563eb;
            }
            
            .weight-highlight {
              font-weight: bold;
              color: #2563eb;
            }
            
            .products-cell {
              max-width: 250px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            
            .footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              height: 15mm;
              border-top: 1px solid #e2e8f0;
              padding-top: 5px;
              font-size: 9px;
              color: #64748b;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .footer-left {
              text-align: left;
            }
            
            .footer-center {
              text-align: center;
            }
            
            .footer-right {
              text-align: right;
            }
            
            .page-number:after {
              content: counter(page);
            }
            
            .total-pages:after {
              content: counter(pages);
            }
            
            /* For browsers that support CSS counters in print */
            @media print {
              .footer {
                position: fixed;
                bottom: 0mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="header-title">${reportTitle}</h1>
            <p class="header-subtitle">${dateRange} • Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
          </div>
          
          <div class="summary-box">
            <div class="summary-item">
              <div class="summary-value">${periodTotals.count}</div>
              <div class="summary-label">Orders</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${periodTotals.orderedQty.toFixed(0)}</div>
              <div class="summary-label">Ordered Qty</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${periodTotals.orderedWeight.toFixed(1)} kg</div>
              <div class="summary-label">Ordered Weight</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${periodTotals.totalCases.toFixed(0)}</div>
              <div class="summary-label">Cases</div>
            </div>
            <div class="summary-item">
              <div class="summary-value highlight">${periodTotals.totalWeight.toFixed(2)} kg</div>
              <div class="summary-label">Net Weight</div>
            </div>
          </div>
          
          ${groupedDispatches.map(({ date, dispatches: dayDispatches }) => `
            <div class="date-section">${date} (${dayDispatches.length} dispatch${dayDispatches.length !== 1 ? 'es' : ''})</div>
            <table>
              <thead>
                <tr>
                  <th>Dispatch #</th>
                  <th>Customer</th>
                  <th>PO Number</th>
                  <th class="right">Cases</th>
                  <th class="right">Net Weight</th>
                  <th>Products</th>
                </tr>
              </thead>
              <tbody>
                ${dayDispatches.map(d => `
                  <tr>
                    <td class="dispatch-num">${d.dispatch_number || '-'}</td>
                    <td>${d.customer_name || '-'}</td>
                    <td>${d.po_number || '-'}</td>
                    <td class="right">${(d.total_cases || 0).toFixed(1)}</td>
                    <td class="right weight-highlight">${(d.total_net_weight || 0).toFixed(2)} kg</td>
                    <td class="products-cell">${d.lines?.map(l => l.product_name).filter(Boolean).join(', ') || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `).join('')}
          
          <div class="footer">
            <div class="footer-left">Cashel Blue Operations Hub</div>
            <div class="footer-center">${reportTitle}</div>
            <div class="footer-right">Page <span class="page-number"></span></div>
          </div>
          
        </body>
      </html>
    `;
    
    openPrintWindow(printContent, { features: 'width=1100,height=800' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dispatch-view-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Dispatch View
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View all dispatches arranged by week
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} data-testid="btn-export-csv">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={exportToPDF} data-testid="btn-export-pdf">
            <Printer className="w-4 h-4 mr-2" />
            Print/PDF
          </Button>
        </div>
      </div>

      {/* Period Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search dispatches..."
                className="pl-10"
                data-testid="input-search-dispatch"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'week' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="btn-view-week"
              >
                Week
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'month' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="btn-view-month"
              >
                Month
              </button>
            </div>

            {/* Period Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goPrev} data-testid="dispatch-view-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-center min-w-[220px]">
                <div className="font-semibold">
                  {viewMode === 'week' ? `Week ${weekNumber}, ${year}` : format(currentDate, 'MMMM yyyy')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {format(rangeStart, 'dd MMM')} - {format(rangeEnd, 'dd MMM yyyy')}
                </div>
              </div>
              
              <Button variant="outline" size="sm" onClick={goNext} data-testid="dispatch-view-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button variant="link" size="sm" onClick={goToCurrent} className="text-xs">
                Today
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ordered vs Production Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total Ordered */}
        <Card>
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">Total Ordered (PO)</p>
            <div className="grid grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold font-mono" data-testid="metric-ordered-dispatches">{periodTotals.count}</p>
                <p className="text-[10px] text-muted-foreground">Orders</p>
              </div>
              <div>
                <p className="text-2xl font-bold font-mono" data-testid="metric-ordered-qty">{periodTotals.orderedQty.toFixed(0)}</p>
                <p className="text-[10px] text-muted-foreground">Units</p>
              </div>
              <div>
                <p className="text-2xl font-bold font-mono" data-testid="metric-ordered-cases">{periodTotals.orderedCases.toFixed(0)}</p>
                <p className="text-[10px] text-muted-foreground">Cases</p>
              </div>
              <div>
                <p className="text-2xl font-bold font-mono" data-testid="metric-ordered-weight">{periodTotals.orderedWeight.toFixed(1)}</p>
                <p className="text-[10px] text-muted-foreground">Weight (kg)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Production */}
        <Card className="border-blue-200 bg-blue-50/40">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-blue-600 font-semibold mb-3">Total Production</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold font-mono" data-testid="metric-prod-cases">{periodTotals.totalCases.toFixed(0)}</p>
                <p className="text-[10px] text-muted-foreground">Cases</p>
              </div>
              <div>
                <p className="text-2xl font-bold font-mono text-blue-700" data-testid="metric-prod-net-weight">{periodTotals.totalWeight.toFixed(1)}</p>
                <p className="text-[10px] text-blue-600">Net Weight (kg)</p>
              </div>
              <div>
                <p className="text-2xl font-bold font-mono" data-testid="metric-prod-gross-weight">{periodTotals.totalGrossWeight.toFixed(1)}</p>
                <p className="text-[10px] text-muted-foreground">Gross Weight (kg)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dispatches by Date */}
      {groupedDispatches.length > 0 ? (
        <div className="space-y-4">
          {groupedDispatches.map(({ date, dispatches: dayDispatches }) => (
            <Card key={date}>
              <CardHeader className="py-3 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-mono flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {date}
                  </CardTitle>
                  <div className="text-sm text-muted-foreground">
                    {dayDispatches.length} dispatch{dayDispatches.length !== 1 ? 'es' : ''}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Dispatch #</th>
                        <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Customer</th>
                        <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">PO Number</th>
                        <th className="text-right px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Cases</th>
                        <th className="text-right px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Net Weight</th>
                        <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Products</th>
                        <th className="text-left px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Invoice</th>
                        <th className="text-center px-4 py-3 text-xs font-medium uppercase text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {dayDispatches.map(dispatch => (
                        <tr 
                          key={dispatch.id} 
                          className="hover:bg-muted/30 cursor-pointer"
                          onClick={() => openDetailModal(dispatch)}
                          data-testid={`dispatch-row-${dispatch.id}`}
                        >
                          <td className="px-4 py-3 font-mono text-sm font-medium text-primary">
                            {dispatch.dispatch_number}
                          </td>
                          <td className="px-4 py-3 text-sm">{dispatch.customer_name}</td>
                          <td className="px-4 py-3 text-sm font-mono">
                            {dispatch.po_number || '-'}
                            {dispatch.fulfilment_label && (
                              <div className="text-[10px] font-sans text-primary" data-testid={`dispatch-view-part-${dispatch.id}`}>{dispatch.fulfilment_label}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-right">
                            {(dispatch.total_cases || 0).toFixed(1)}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-right font-semibold text-blue-700">
                            {(dispatch.total_net_weight || 0).toFixed(2)} kg
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {getProductSummary(dispatch)}
                          </td>
                          <td className="px-4 py-3 text-xs" onClick={(e) => e.stopPropagation()}>
                            {/* AccountsIQ status chip — flips green once the
                                operator marks the dispatch as invoiced. */}
                            {dispatch.invoice_number ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-mono cursor-pointer hover:bg-emerald-200"
                                onClick={() => { setAccountantDispatch(dispatch); setAccountantOpen(true); }}
                                title={`Invoiced ${dispatch.invoice_raised_at ? new Date(dispatch.invoice_raised_at).toLocaleString('en-IE') : ''} — click to view / edit`}
                                data-testid={`invoice-chip-${dispatch.id}`}
                              >
                                <CircleCheck className="w-3 h-3" />
                                {dispatch.invoice_number}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 hover:bg-amber-200"
                                onClick={() => { setAccountantDispatch(dispatch); setAccountantOpen(true); }}
                                title="Click to record the AccountsIQ invoice number"
                                data-testid={`invoice-pending-${dispatch.id}`}
                              >
                                Pending
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => openDetailModal(dispatch)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => viewDispatchPDF(dispatch.id)}
                                title="View PDF"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => printDispatchPDF(dispatch.id)}
                                title="Print Dispatch Note"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => openCoaPdf(dispatch.id)}
                                title="Certificate of Analysis"
                                data-testid={`coa-pdf-${dispatch.dispatch_number}`}
                              >
                                <ScrollText className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    title="Print Pallet Labels"
                                    data-testid={`pallet-labels-${dispatch.dispatch_number}`}
                                  >
                                    <Tags className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => { setAccountantDispatch(dispatch); setAccountantOpen(true); }} data-testid={`btn-accountant-view-${dispatch.id}`}>
                                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                                    AccountsIQ View
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setAiqPreviewDispatch(dispatch)} data-testid={`btn-aiq-preview-${dispatch.id}`}>
                                    <Send className="w-4 h-4 mr-2 text-[#005F73]" />
                                    Preview &amp; send to AccountsIQ
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => navigate(`/pallet-labels/${dispatch.id}`)}>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Standard (Designer)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openPalletLabels(dispatch.id, 2, { template: 'agropur' })}>
                                    <Package className="w-4 h-4 mr-2" />
                                    Agropur
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setAldiBranchDispatchId(dispatch.id);
                                    setAldiBranchOpen(true);
                                  }}>
                                    <Package className="w-4 h-4 mr-2" />
                                    Aldi
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Truck className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No dispatches found for this week</p>
            {searchTerm && (
              <p className="text-sm text-muted-foreground mt-2">
                Try adjusting your search term
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dispatch Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="font-mono text-lg">{selectedDispatch?.dispatch_number}</span>
              <span className={`text-xs px-2 py-1 rounded ${
                selectedDispatch?.status === 'dispatched' ? 'bg-emerald-100 text-emerald-700' :
                selectedDispatch?.status === 'locked' ? 'bg-blue-100 text-blue-700' :
                'bg-zinc-100 text-zinc-700'
              }`}>
                {selectedDispatch?.status?.toUpperCase()}
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedDispatch && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedDispatch.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Dispatch Date</p>
                  <p className="font-mono">{selectedDispatch.dispatch_date}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">PO Number</p>
                  <p className="font-mono">{selectedDispatch.po_number || '-'}</p>
                  {selectedDispatch.fulfilment_label && (
                    <p className="text-xs text-primary mt-0.5" data-testid="dispatch-view-fulfilment">
                      Fulfilment: {selectedDispatch.fulfilment_label}
                      {selectedDispatch.po_original_order && selectedDispatch.po_original_order !== selectedDispatch.dispatch_number
                        ? ` · orig. #${selectedDispatch.po_original_order}` : ''}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Transport</p>
                  <p>{selectedDispatch.vehicle_info || '-'}</p>
                </div>
              </div>

              {/* Totals — Ordered vs Dispatched */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/40 rounded-lg">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-2">Ordered</p>
                  <div className="flex gap-4">
                    <div className="text-center flex-1">
                      <p className="text-xl font-bold font-mono">{selectedDispatch.total_ordered_qty ?? computeTotal(selectedDispatch, 'quantity')}</p>
                      <p className="text-[10px] text-muted-foreground">Units</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-xl font-bold font-mono">{selectedDispatch.total_ordered_cases ?? computeTotal(selectedDispatch, 'cases')}</p>
                      <p className="text-[10px] text-muted-foreground">Cases</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-xl font-bold font-mono">{(selectedDispatch.total_ordered_weight || 0).toFixed(1)}</p>
                      <p className="text-[10px] text-muted-foreground">Weight (kg)</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-[10px] uppercase tracking-wide text-blue-600 font-medium mb-2">Production</p>
                  <div className="flex gap-4">
                    <div className="text-center flex-1">
                      <p className="text-xl font-bold font-mono">{(selectedDispatch.total_cases || 0).toFixed(0)}</p>
                      <p className="text-[10px] text-muted-foreground">Cases</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-xl font-bold font-mono text-blue-700">{(selectedDispatch.total_net_weight || 0).toFixed(2)}</p>
                      <p className="text-[10px] text-blue-600">Net Weight (kg)</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-xl font-bold font-mono">{(selectedDispatch.total_gross_weight || 0).toFixed(2)}</p>
                      <p className="text-[10px] text-muted-foreground">Gross (kg)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Line Items ({selectedDispatch.lines?.length || 0})</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Product</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Supp Code</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Batch</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Use By</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Ordered</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Cases</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Net Wt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedDispatch.lines?.map((line, idx) => {
                        const cases = line.cases_override ?? line.calculated_cases ?? line.cases ?? 0;
                        const netWt = line.weight_override ?? line.total_weight ?? 0;
                        return (
                          <tr key={idx}>
                            <td className="px-3 py-2">{line.product_name}</td>
                            <td className="px-3 py-2 font-mono text-xs">{line.supplier_product_code || '-'}</td>
                            <td className="px-3 py-2 font-mono text-xs">{line.batch_code || '-'}</td>
                            <td className={`px-3 py-2 font-mono text-xs ${line.use_by_date === 'No Date' ? 'text-amber-600 italic' : ''}`}>
                              {line.use_by_date === 'No Date' ? 'No Date' : (line.use_by_date || '-')}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-muted-foreground">{line.quantity || 0}</td>
                            <td className="px-3 py-2 text-right font-mono">{Number(cases).toFixed(1)}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-blue-700">
                              {Number(netWt).toFixed(2)} kg
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              {selectedDispatch.notes && (
                <div>
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="text-sm mt-1 p-2 bg-muted/30 rounded">{selectedDispatch.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>
            <Button 
              variant="outline"
              onClick={() => viewDispatchPDF(selectedDispatch?.id)}
            >
              <FileText className="w-4 h-4 mr-2" />
              View PDF
            </Button>
            <Button 
              variant="outline"
              onClick={() => printDispatchPDF(selectedDispatch?.id)}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print PDF
            </Button>
            <Button onClick={() => {
              setDetailModalOpen(false);
              navigate(`/dispatch/${selectedDispatch?.id}`);
            }}>
              Edit Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AldiBranchDialog
        open={aldiBranchOpen}
        onClose={() => setAldiBranchOpen(false)}
        dispatchId={aldiBranchDispatchId}
      />

      <AccountantViewDialog
        open={accountantOpen}
        onOpenChange={setAccountantOpen}
        dispatch={accountantDispatch}
        onInvoiceMarked={(dispatchId, data) => {
          // Optimistically patch the local list so the chip updates
          // without a full reload — the operator immediately sees the
          // dispatch flip from "Pending" to the green invoice chip.
          setDispatches((prev) => prev.map((d) =>
            d.id === dispatchId
              ? { ...d, ...data, dispatch_number: d.dispatch_number }
              : d
          ));
          setAccountantDispatch((d) => d && d.id === dispatchId ? { ...d, ...data } : d);
        }}
      />
      <InvoicePreviewDialog
        open={!!aiqPreviewDispatch}
        onOpenChange={(v) => { if (!v) setAiqPreviewDispatch(null); }}
        dispatch={aiqPreviewDispatch}
        onSent={(dispatchId, patch) => setDispatches((prev) => prev.map((d) => (d.id === dispatchId ? { ...d, ...patch } : d)))}
      />
    </div>
  );
}
